import { makeNamespacedLog } from '@sb/log';

import type { AbstractHeartbeatCommand } from './AbstractHeartbeatCommand';

const log = makeNamespacedLog('HeartbeatManager');

/**
 * Manages lifecycle of heartbeat commands.
 *
 * Context/assumptions:
 *  - Commands are created then registered with the manager.
 *  - Commands have both a heartbeat and expiration.
 *    - Heartbeat is used to check if the command is still active. If no heartbeat is received, the command is canceled but not destroyed (see expiry).
 *    - Expiration is used to so that commands may still be accessed after completion/after a command is no longer necessary. For example, if a command results in a failure, will still want to be able to access the command to get the failure details for some period after the command has completed.
 *  - Relatively low number of commands: We have 2 timers per command. If we start to see large numbers of commands, might make sense to centralize the timers into a single timer.
 */
export class HeartbeatManager {
  private readonly EXPIRY_INTERVAL: Readonly<number> = 5000; // Check for command expiry every 5 seconds

  public commands = new Map<string, AbstractHeartbeatCommand<any>>();

  private expiryTimers = new Map<string, NodeJS.Timeout>();

  private heartbeatTimers = new Map<string, NodeJS.Timeout>();

  // Periodically check for command growth
  private readonly MAX_COMMANDS: Readonly<number> = 100;

  private readonly COMMAND_MONITORING_INTERVAL: Readonly<number> = 60000; // Check every minute

  private monitoringTimer: NodeJS.Timeout | null = null;

  private lastLoggedCommandCount: number | null = null;

  public constructor() {
    this.monitoringTimer = setInterval(
      () => this.monitorCommandGrowth(),
      this.COMMAND_MONITORING_INTERVAL,
    );
  }

  public register(command: AbstractHeartbeatCommand<any>) {
    const { id } = command;

    this.commands.set(id, command);

    command.heartbeat();

    this.expiryTimers.set(id, this.generateExpiryTimer(id));

    this.heartbeatTimers.set(
      id,
      this.generateHeartbeatTimer(command.id, command.heartbeatInterval),
    );

    // Fresh at time of registration
    command.heartbeat();

    return command;
  }

  public setHeartbeat(id: string) {
    const command = this.commands.get(id);

    if (command == null) {
      log.warn('setHeartbeat', `Command with id ${id} not found`, { id });

      return;
    }

    command.heartbeat();
  }

  public destroy() {
    log.info('destroy', `Destroying HeartbeatCommander`);

    const allIds = new Set<string>([
      ...this.commands.keys(),
      ...this.expiryTimers.keys(),
      ...this.heartbeatTimers.keys(),
    ]);

    allIds.forEach((id) => this.destroyCommand(id));

    // Stop monitoring timer
    if (this.monitoringTimer) {
      clearInterval(this.monitoringTimer);
      this.monitoringTimer = null;
    }

    (this.commands as unknown) = null;
    (this.expiryTimers as unknown) = null;
    (this.heartbeatTimers as unknown) = null;
  }

  private generateHeartbeatTimer(
    id: string,
    heartbeatInterval: number,
  ): NodeJS.Timeout {
    return setInterval(() => {
      const cmd = this.commands.get(id);

      if (cmd == null) {
        log.error(
          'heartbeatTimer',
          `Command for ID not found. This is an unexpected error. Expiring command.`,
          { id },
        );

        this.destroyCommand(id);

        return;
      }

      // Stop checking if the command is not active/doesn't require a heartbeat
      if (!cmd.isActive) {
        log.debug(
          'heartbeatTimer',
          `Command for ID is not active. Removing heartbeat timer.`,
          { id },
        );

        const hbTimer = this.heartbeatTimers.get(id);

        if (hbTimer != null) {
          clearTimeout(hbTimer);
        }

        this.heartbeatTimers.delete(id);

        return;
      }

      // Check for heartbeat
      if (cmd.isHeartbeatTimedOut) {
        log.info(
          'heartbeatTimer',
          `Command for ID heartbeat timed out. Cancelling.`,
          { id },
        );

        const hbTimer = this.heartbeatTimers.get(id);

        if (hbTimer != null) {
          clearTimeout(hbTimer);
        }

        this.heartbeatTimers.delete(id);

        try {
          cmd.cancel();
        } catch (error) {
          log.error('heartbeatTimer', `Error cancelling command.`, {
            id,
            error,
          });
        }
      }
    }, heartbeatInterval);
  }

  private generateExpiryTimer(id: string): NodeJS.Timeout {
    return setInterval(() => {
      const cmd = this.commands.get(id);

      if (cmd == null) {
        log.warn('create.expiryTimer', `Command for ID not found. Expiring.`, {
          id,
        });

        this.destroyCommand(id);

        return;
      }

      if (cmd.isExpired) {
        log.info('create.expiryTimer', `Command for ID expired. Expiring.`, {
          id,
        });

        this.destroyCommand(id);
      }
    }, this.EXPIRY_INTERVAL);
  }

  /**
   * Destroys a single command and all associated timers.
   * @param id Command ID
   */
  private destroyCommand(id: string) {
    log.info('destroyCommand', `Destroying command with id ${id}.`, { id });
    const expiryTimer = this.expiryTimers.get(id);
    const hbTimer = this.heartbeatTimers.get(id);
    const cmd = this.commands.get(id);

    if (expiryTimer != null) {
      clearTimeout(expiryTimer);
    } else {
      log.debug(
        'destroyCommand',
        `Expiry timer for command with id not found. Not clearing timeout.`,
        { id },
      );
    }

    this.expiryTimers.delete(id);

    // Clean up heartbeat if it still exists
    if (hbTimer != null) {
      log.warn(
        'destroyCommand',
        `Heartbeat timer for command with id found. Command may not be complete.`,
        { id },
      );

      clearTimeout(hbTimer);
    } else {
      log.debug(
        'destroyCommand',
        `Heartbeat timer for command with id not found. Not clearing timeout.`,
        { id },
      );
    }

    this.heartbeatTimers.delete(id);

    // Destroy the command
    if (cmd == null) {
      log.warn('destroyCommand', `Command ID not found. Skipping destroy.`, {
        id,
      });
    } else {
      cmd.destroy();
    }

    // Delete the command from the map
    this.commands.delete(id);
  }

  private monitorCommandGrowth(): void {
    const commandCount = this.commands.size;

    // Log current state for monitoring, only when the count changes
    if (commandCount !== this.lastLoggedCommandCount) {
      this.lastLoggedCommandCount = commandCount;

      log.debug('monitorCommandGrowth', 'Current command count', {
        commandCount,
        expiryTimerCount: this.expiryTimers.size,
        heartbeatTimerCount: this.heartbeatTimers.size,
      });
    }

    // Alert if approaching limits
    if (commandCount > this.MAX_COMMANDS * 0.8) {
      log.warn('monitorCommandGrowth', 'Command count approaching maximum', {
        maxCommands: this.MAX_COMMANDS,
        commandCount,
      });
    }

    // Take action if exceeding limits
    if (commandCount > this.MAX_COMMANDS) {
      log.error('monitorCommandGrowth', 'Command count exceeded maximum', {
        maxCommands: this.MAX_COMMANDS,
        commandCount,
      });

      this.cleanupOldestCommands(commandCount - this.MAX_COMMANDS);
    }

    // Check for timer/command mismatch (potential leak)
    if (
      this.expiryTimers.size !== commandCount ||
      this.heartbeatTimers.size > commandCount
    ) {
      log.warn('monitorCommandGrowth', 'Timer count mismatch detected', {
        commandCount,
        expiryTimerCount: this.expiryTimers.size,
        heartbeatTimerCount: this.heartbeatTimers.size,
      });

      // Self-heal by cleaning up orphaned timers
      this.cleanupOrphanedTimers();
    }
  }

  private cleanupOldestCommands(count: number): void {
    // Find oldest commands by last heartbeat time.
    Array.from(this.commands.entries())
      .map(([id, cmd]) => ({ id, lastHeartbeat: cmd.lastHeartbeat.getTime() }))
      .sort((a, b) => a.lastHeartbeat - b.lastHeartbeat)
      .slice(0, count)
      .forEach(({ id }) => this.destroyCommand(id));
  }

  private cleanupOrphanedTimers(): void {
    const validCommandIds = new Set(this.commands.keys());

    // Clean up expiry timers
    this.expiryTimers.forEach((timer, id) => {
      if (!validCommandIds.has(id)) {
        clearTimeout(timer);
        this.expiryTimers.delete(id);

        log.info('cleanupOrphanedTimers', 'Cleaned up orphaned expiry timer', {
          id,
        });
      }
    });

    // Clean up heartbeat timers
    this.heartbeatTimers.forEach((timer, id) => {
      if (!validCommandIds.has(id)) {
        clearTimeout(timer);
        this.heartbeatTimers.delete(id);

        log.info(
          'cleanupOrphanedTimers',
          'Cleaned up orphaned heartbeat timer',
          { id },
        );
      }
    });
  }
}
